import { useCallback, useEffect, useRef, useState } from 'react';

export interface DeviceVoicePreferences {
  voiceOutputEnabled: boolean;
  assistantVoice: string;
  speechRate: number;
  voicePitch: number;
  voiceVolume: number;
  voiceLanguage: string;
  continuousListening: boolean;
}

interface RecognitionResult {
  isFinal: boolean;
  0: { transcript: string };
}
interface RecognitionEvent {
  resultIndex: number;
  results: ArrayLike<RecognitionResult>;
}
interface DeviceRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  abort: () => void;
}
type RecognitionConstructor = new () => DeviceRecognition;

export function useDeviceVoices() {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  useEffect(() => {
    const synthesis = window.speechSynthesis;
    if (!synthesis) return;
    const refresh = () => setVoices(synthesis.getVoices());
    refresh();
    synthesis.addEventListener('voiceschanged', refresh);
    return () => synthesis.removeEventListener('voiceschanged', refresh);
  }, []);
  return voices;
}

function recognitionConstructor(): RecognitionConstructor | undefined {
  const browser = window as unknown as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return browser.SpeechRecognition ?? browser.webkitSpeechRecognition;
}

/** Browser-managed speech. No platform API call or microphone request happens on mount. */
export function useDeviceVoice({
  preferences,
  enabled,
  scopeKey = '',
  onFinalTranscript,
}: {
  preferences: DeviceVoicePreferences;
  enabled: boolean;
  scopeKey?: string;
  onFinalTranscript?: (text: string) => void;
}) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = useRef({ preferences, enabled, scopeKey, onFinalTranscript });
  current.current = { preferences, enabled, scopeKey, onFinalTranscript };
  const recognition = useRef<DeviceRecognition | null>(null);
  const wantsListening = useRef(false);
  const restartTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shortRestarts = useRef(0);
  const recognitionStartedAt = useRef(0);
  const utterance = useRef<SpeechSynthesisUtterance | null>(null);
  const speaking = useRef(false);
  const playbackPause = useRef(0);

  const clearRestart = useCallback(() => {
    if (restartTimer.current !== null) clearTimeout(restartTimer.current);
    restartTimer.current = null;
  }, []);

  const stopListening = useCallback(() => {
    playbackPause.current += 1;
    wantsListening.current = false;
    clearRestart();
    const active = recognition.current;
    recognition.current = null;
    if (active) {
      active.onresult = null;
      active.onerror = null;
      active.onend = null;
      active.abort();
    }
    setListening(false);
  }, [clearRestart]);

  const stopSpeaking = useCallback(() => {
    playbackPause.current += 1;
    const active = utterance.current;
    utterance.current = null;
    speaking.current = false;
    if (active) {
      active.onend = null;
      active.onerror = null;
      window.speechSynthesis?.cancel();
    }
  }, []);

  const pauseListeningForPlayback = useCallback(() => {
    clearRestart();
    const token = ++playbackPause.current;
    const paused = recognition.current;
    const scope = current.current.scopeKey;
    const optedIn = wantsListening.current && current.current.preferences.continuousListening;
    if (!current.current.preferences.continuousListening) wantsListening.current = false;
    speaking.current = true;
    if (paused) {
      paused.abort();
      setListening(false);
    }
    return (resume = true) => {
      if (playbackPause.current !== token) return;
      playbackPause.current += 1;
      speaking.current = false;
      if (
        !resume ||
        !optedIn ||
        !paused ||
        recognition.current !== paused ||
        !wantsListening.current ||
        !current.current.enabled ||
        current.current.scopeKey !== scope ||
        !current.current.preferences.continuousListening ||
        document.visibilityState === 'hidden'
      )
        return;
      try {
        paused.start();
        recognitionStartedAt.current = Date.now();
        setListening(true);
      } catch {
        wantsListening.current = false;
        setError('Speech recognition paused. Click the microphone to resume.');
      }
    };
  }, [clearRestart]);

  const startListening = useCallback(() => {
    if (!current.current.enabled) return false;
    const Constructor = recognitionConstructor();
    if (!Constructor) {
      setError(
        'Browser speech recognition is unavailable. Use a supported browser or a recorded voice message.',
      );
      return false;
    }
    stopSpeaking();
    stopListening();
    setError(null);
    const active = new Constructor();
    const settings = current.current.preferences;
    active.lang = settings.voiceLanguage;
    active.continuous = settings.continuousListening;
    active.interimResults = false;
    recognition.current = active;
    wantsListening.current = true;
    shortRestarts.current = 0;
    active.onresult = (event) => {
      if (
        !current.current.enabled ||
        !wantsListening.current ||
        speaking.current ||
        recognition.current !== active
      )
        return;
      const finalText: string[] = [];
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (result?.isFinal && result[0].transcript.trim())
          finalText.push(result[0].transcript.trim());
      }
      if (finalText.length > 0) current.current.onFinalTranscript?.(finalText.join(' '));
    };
    active.onerror = (event) => {
      if (recognition.current !== active) return;
      // abort() is expected while our own playback holds recognition paused.
      if (event.error === 'aborted' && speaking.current) return;
      // A denial or service error must never trigger a new permission prompt loop.
      if (event.error !== 'no-speech') {
        wantsListening.current = false;
        clearRestart();
        setListening(false);
        setError(
          event.error === 'not-allowed' || event.error === 'service-not-allowed'
            ? 'Microphone access was denied. Allow microphone access in this browser, then click the microphone again.'
            : 'Speech recognition stopped. Click the microphone to try again.',
        );
      }
    };
    const start = () => {
      if (!wantsListening.current || !current.current.enabled || recognition.current !== active)
        return;
      try {
        active.lang = current.current.preferences.voiceLanguage;
        active.continuous = current.current.preferences.continuousListening;
        recognitionStartedAt.current = Date.now();
        active.start();
        setListening(true);
      } catch {
        wantsListening.current = false;
        setListening(false);
        setError('Unable to start speech recognition. Click the microphone to try again.');
      }
    };
    active.onend = () => {
      if (recognition.current !== active) return;
      setListening(false);
      if (!speaking.current && !current.current.preferences.continuousListening)
        wantsListening.current = false;
      if (
        !wantsListening.current ||
        !current.current.enabled ||
        speaking.current ||
        !current.current.preferences.continuousListening
      )
        return;
      shortRestarts.current =
        Date.now() - recognitionStartedAt.current < 1000 ? shortRestarts.current + 1 : 0;
      if (shortRestarts.current > 3) {
        wantsListening.current = false;
        setError('Speech recognition stopped repeatedly. Click the microphone to try again.');
        return;
      }
      restartTimer.current = setTimeout(start, 300);
    };
    start();
    return true;
  }, [clearRestart, stopListening, stopSpeaking]);

  const speak = useCallback(
    (text: string, options: { manual?: boolean } = {}) => {
      const settings = current.current.preferences;
      if (!current.current.enabled || (!options.manual && !settings.voiceOutputEnabled))
        return false;
      if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === 'undefined') {
        setError('Device speech playback is unavailable in this browser.');
        return false;
      }
      const content = text.trim().slice(0, 12000);
      if (!content) return false;
      stopSpeaking();
      clearRestart();
      speaking.current = true;
      // Avoid dictating Helios's own reply into the user's next message.
      const pausedRecognition = recognition.current;
      if (!settings.continuousListening) wantsListening.current = false;
      if (pausedRecognition) {
        pausedRecognition.abort();
        setListening(false);
      }
      const voice = new SpeechSynthesisUtterance(content);
      voice.voice =
        window.speechSynthesis
          .getVoices()
          .find((item) => item.voiceURI === settings.assistantVoice) ?? null;
      voice.lang = voice.voice?.lang ?? settings.voiceLanguage;
      voice.rate = settings.speechRate;
      voice.pitch = settings.voicePitch;
      voice.volume = settings.voiceVolume;
      utterance.current = voice;
      const finish = () => {
        if (utterance.current !== voice) return;
        utterance.current = null;
        speaking.current = false;
        if (
          pausedRecognition &&
          recognition.current === pausedRecognition &&
          wantsListening.current &&
          current.current.enabled &&
          current.current.preferences.continuousListening
        ) {
          try {
            pausedRecognition.start();
            recognitionStartedAt.current = Date.now();
            setListening(true);
          } catch {
            wantsListening.current = false;
            setError('Speech recognition paused. Click the microphone to resume.');
          }
        }
      };
      voice.onend = finish;
      voice.onerror = () => {
        setError('Device speech could not play. Check your browser speech settings.');
        finish();
      };
      setError(null);
      try {
        window.speechSynthesis.speak(voice);
        return true;
      } catch {
        setError('Device speech could not play. Check your browser speech settings.');
        finish();
        return false;
      }
    },
    [clearRestart, stopSpeaking],
  );

  useEffect(() => {
    if (!enabled) {
      stopListening();
      stopSpeaking();
    }
  }, [enabled, stopListening, stopSpeaking]);

  useEffect(() => {
    stopListening();
    stopSpeaking();
  }, [scopeKey, stopListening, stopSpeaking]);

  useEffect(() => {
    if (!preferences.voiceOutputEnabled) stopSpeaking();
  }, [preferences.voiceOutputEnabled, stopSpeaking]);

  useEffect(() => {
    if (recognition.current) recognition.current.continuous = preferences.continuousListening;
  }, [preferences.continuousListening]);

  useEffect(() => {
    const stopWhenHidden = () => {
      if (document.visibilityState === 'hidden') {
        stopListening();
        stopSpeaking();
      }
    };
    document.addEventListener('visibilitychange', stopWhenHidden);
    return () => {
      document.removeEventListener('visibilitychange', stopWhenHidden);
      stopListening();
      stopSpeaking();
    };
  }, [stopListening, stopSpeaking]);

  return {
    listening,
    error,
    speechSupported: Boolean(
      window.speechSynthesis && typeof SpeechSynthesisUtterance !== 'undefined',
    ),
    recognitionSupported: Boolean(recognitionConstructor()),
    speak,
    stopSpeaking,
    startListening,
    stopListening,
    pauseListeningForPlayback,
  };
}

/** Only announces a changed context after opt-in, with a cooldown and no provider calls. */
export function useDeviceNarration({
  enabled,
  context,
  speak,
}: {
  enabled: boolean;
  context: string;
  speak: (text: string) => boolean;
}) {
  const lastContext = useRef(context);
  const lastSpoken = useRef(0);
  const selectionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSelection = useRef('');
  useEffect(() => {
    if (lastContext.current === context) return;
    lastContext.current = context;
    if (enabled && Date.now() - lastSpoken.current >= 8000) {
      if (speak(`Now viewing ${context.slice(0, 100)}.`)) lastSpoken.current = Date.now();
    }
  }, [context, enabled, speak]);
  useEffect(() => {
    if (!enabled) return;
    const announceSelection = () => {
      if (selectionTimer.current !== null) clearTimeout(selectionTimer.current);
      selectionTimer.current = setTimeout(() => {
        const selection =
          window.getSelection()?.toString().trim().replace(/\s+/g, ' ').slice(0, 160) ?? '';
        if (
          !selection ||
          selection === lastSelection.current ||
          Date.now() - lastSpoken.current < 8000
        )
          return;
        lastSelection.current = selection;
        if (speak(`Selected: ${selection}.`)) lastSpoken.current = Date.now();
      }, 700);
    };
    document.addEventListener('selectionchange', announceSelection);
    return () => {
      document.removeEventListener('selectionchange', announceSelection);
      if (selectionTimer.current !== null) clearTimeout(selectionTimer.current);
    };
  }, [enabled, speak]);
}
